import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  MapPin, Calendar, ArrowRight, Check, Download, FileText, Leaf,
  Factory, Fuel, Car, Cog, Landmark, TrendingUp, Wheat, Building2,
  Users, Mic2, BarChart3, Handshake, Sparkles, Quote,
} from "lucide-react";
import { Megaphone } from "lucide-react";
import { Reveal, Stagger, StaggerItem, Overline, SectionHeading, CTAButton, ArrowLink, AnimatedNumber } from "@/components/primitives";
import { CtaStrip } from "@/components/sections";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HOME_FAQS } from "@/lib/seo";
import LeadDialog, { DOCS } from "@/components/forms/LeadDialog";
import CalculatorTool from "@/components/CalculatorTool";
import PartnersMarquee from "@/components/PartnersMarquee";
import { BlurText, SpotlightCard } from "@/components/fancy";
import { EVENT, REG_TARIFF, REG_WINDOW_LABELS, currentRegWindow } from "@/data/site";
import { cn } from "@/lib/utils";

const HERO_IMG = "/assets/site/hero-ethanol.jpg";

const STAT_BAR = [
  { icon: Users, value: 800, suffix: "+", label: "Delegates" },
  { icon: Mic2, value: 30, suffix: "+", label: "Speakers" },
  { icon: Building2, value: 50, suffix: "+", label: "Companies" },
  { icon: BarChart3, value: 5, suffix: "", label: "Business Sessions" },
  { icon: Handshake, display: "1:1", label: "B2B Meetings" },
  { icon: Sparkles, display: "2-in-1", label: "Conference + Expo" },
];

const AUDIENCE = [
  { icon: Factory, label: "Ethanol Producers" },
  { icon: Fuel, label: "Oil Marketing Companies" },
  { icon: Car, label: "Automakers & OEMs" },
  { icon: Cog, label: "Technology Providers" },
  { icon: TrendingUp, label: "Investors & Financiers" },
  { icon: Landmark, label: "Policymakers & Regulators" },
  { icon: Wheat, label: "Agri Feedstock Suppliers" },
];

const THEMES = [
  "India's Post-E20 Policy & Roadmap",
  "Scaling Ethanol Production Sustainably",
  "Feedstock Diversification & Innovation",
  "2G Ethanol & Advanced Biofuels",
  "Bioenergy, CBG & Circular Economy",
  "Global Markets & Investment Outlook",
  "Technology, Safety & Infrastructure",
  "Financing the Biofuel Transition",
];

const AGENDA_FLOW = [
  { day: "Day 1 · 28 Sep 2026", items: ["Inaugural & Keynotes", "Policy & Market Outlook", "Feedstock & Flex-Fuel Sessions", "Leadership Awards & Gala Dinner"] },
  { day: "Day 2 · 29 Sep 2026", items: ["Global Ethanol Markets & Trade", "SAF, Bio-methanol & CBG", "Expo & B2B Meetings", "Vision 2030 Declaration"] },
];

const PROOF = [
  { stat: "E30", line: "Fuel standards for blends up to E30 were notified in May 2026 (IS 19850:2026) — the rulebook for the next phase just landed.", src: "Ministry of Petroleum & Natural Gas, 2026" },
  { stat: "E20 · 2025", line: "India hit 20% ethanol blending in 2025 — five years ahead of its 2030 target, up from 1.5% in 2014.", src: "PIB, 2025" },
  { stat: "1% SAF", line: "A 1% sustainable aviation fuel target by 2027 opens an alcohol-to-jet pathway off India's ethanol surplus.", src: "Policy roadmap, 2026" },
];

const RESOURCES = [
  { icon: FileText, title: "Event Brochure", desc: "The full overview of the forum, expo and awards — everything in one PDF.", cta: "Get the brochure", formType: "brochure", doc: "brochure" },
  { icon: Megaphone, title: "Sponsorship Proposal", desc: "The official 2026 proposal — tiers, stall options, branding and partnership ROI in one PDF.", cta: "Get the proposal", formType: "sponsorship_proposal", doc: "sponsorProposal" },
];

function useCountdown(targetIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, new Date(targetIso).getTime() - now);
  return { days: Math.floor(diff / 86400000) };
}

function money(n, cur) {
  return cur === "INR" ? "₹" + n.toLocaleString("en-IN") : "$" + n.toLocaleString("en-US");
}

function Hero() {
  const { days } = useCountdown("2026-09-28T10:00:00+05:30");
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-navy grain pt-32 pb-16 md:pt-40 md:pb-24">
      <div className="dotgrid-dark pointer-events-none absolute inset-0 opacity-50" />
      <div className="pointer-events-none absolute -top-40 right-1/4 h-[28rem] w-[28rem] rounded-full bg-lime/15 blur-[150px]" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {/* LEFT */}
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-lime/40 bg-lime/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-lime">
                Conference + Expo
              </span>
            </Reveal>

            <Reveal delay={0.08}>
              <BlurText
                as="h1"
                delay={0.12}
                text="India's Ethanol Economy & Biofuel Leadership Forum"
                data-testid="hero-h1"
                className="mt-6 max-w-xl text-balance font-display text-4xl font-semibold leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-[3.6rem]"
              />
            </Reveal>

            <Reveal delay={0.14}>
              <p className="mt-5 font-display text-2xl font-medium tracking-tight text-gradient sm:text-3xl">
                Produce More. Blend Higher. Power Bharat.
              </p>
            </Reveal>

            <Reveal delay={0.2}>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300">
                A two-day leadership conference and a concurrent technology expo — uniting India's producers, oil companies, automakers, technologists, investors and policymakers to shape the post-E20 roadmap.
              </p>
            </Reveal>

            <Reveal delay={0.24}>
              <div className="mt-7 flex flex-wrap items-stretch gap-x-8 gap-y-5 rounded-2xl border border-white/10 bg-white/[0.04] px-6 py-5">
                <div className="flex items-center gap-3.5">
                  <Calendar className="h-8 w-8 shrink-0 text-lime" />
                  <div>
                    <p className="font-display text-2xl font-bold leading-none text-white sm:text-3xl">28–29 Sep 2026</p>
                    <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Two days · Conference + Expo</p>
                  </div>
                </div>
                <div className="hidden w-px bg-white/15 sm:block" />
                <div className="flex items-center gap-3.5">
                  <MapPin className="h-8 w-8 shrink-0 text-lime" />
                  <div>
                    <p className="font-display text-2xl font-bold leading-none text-white sm:text-3xl">Westin Powai Lake</p>
                    <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Mumbai, India</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.26}>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <CTAButton to="/register" variant="primary" data-testid="hero-register-btn" className="px-8 py-4">
                  Book Your Pass <ArrowRight className="h-4 w-4" />
                </CTAButton>
                <CTAButton to="/exhibit" variant="outline" data-testid="hero-exhibit-btn" className="border-white/25 px-8 py-4 text-white hover:bg-white/10">
                  Exhibit / Sponsor
                </CTAButton>
              </div>
            </Reveal>

            <Reveal delay={0.32}>
              <p className="mt-7 flex items-center gap-2 border-t border-white/10 pt-5 text-sm text-slate-400">
                <Check className="h-4 w-4 text-lime" />
                Organised by <strong className="font-semibold text-white">Tefla's</strong> · 30 years convening India's energy &amp; agri industries
              </p>
            </Reveal>
          </div>

          {/* RIGHT — image with overlays */}
          <Reveal delay={0.2}>
            <div className="relative">
              <div className="relative aspect-[4/3] overflow-hidden rounded-[2rem] border border-white/10 shadow-2xl">
                <img src={HERO_IMG} alt="India's ethanol economy" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
              </div>

              {/* POST E20 badge */}
              <motion.div
                data-testid="hero-badge"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -right-3 -top-5 flex h-24 w-24 flex-col items-center justify-center rounded-full border-2 border-navy bg-lime text-center shadow-xl sm:h-28 sm:w-28"
              >
                <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-navy/70">Post</span>
                <span className="font-display text-2xl font-bold leading-none text-navy sm:text-3xl">E20</span>
                <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-navy/70">Roadmap</span>
              </motion.div>

              {/* glass card */}
              <div className="glass absolute -bottom-5 left-4 right-4 rounded-2xl border border-white/15 bg-navy/70 p-5 sm:left-8 sm:right-8">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-lime/20 text-lime"><Leaf className="h-5 w-5" /></span>
                  <div>
                    <p className="font-display text-base font-semibold text-white">Be Part of India's Energy Transition</p>
                    <p className="mt-0.5 text-xs text-slate-300">Connect. Collaborate. Create impact — the future of fuel starts here.</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 border-t border-white/10 pt-3 text-xs font-semibold text-lime">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-lime" /> Begins in {days} days · {EVENT.venue}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function StatsBar() {
  return (
    <section data-testid="stats-bar" className="border-y border-white/10 bg-navy2">
      <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/10 px-6 sm:grid-cols-3 md:px-12 lg:grid-cols-6 lg:divide-y-0">
        {STAT_BAR.map((s) => (
          <div key={s.label} data-testid="stat-item" className="flex flex-col items-center px-2 py-7 text-center">
            <s.icon className="h-5 w-5 text-lime" />
            <div className="mt-2 font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">
              {s.display ? s.display : <AnimatedNumber value={s.value} suffix={s.suffix || ""} />}
            </div>
            <p className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-400">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Pricing() {
  const win = currentRegWindow();
  const w = REG_WINDOW_LABELS[win];
  const [cur, setCur] = useState("INR");
  const tiers = Object.entries(REG_TARIFF).map(([key, t]) => ({
    key, label: t.label, sub: t.sub,
    price: (cur === "INR" ? t.inr : t.usd)[win],
    popular: key === "stay1",
    includes: key === "conference"
      ? ["All conference sessions", "Concurrent expo access", "Networking lunches & hi-tea"]
      : key === "stay1"
      ? ["All Conference Pass benefits", "1 night at Westin Powai Lake, Mumbai", "Awards gala dinner access"]
      : ["All Conference Pass benefits", "2 nights at Westin Powai Lake, Mumbai", "Awards gala dinner access"],
  }));
  return (
    <section data-testid="pricing-section" id="pricing" className="bg-parchment py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <Overline className="mb-3">Book your pass</Overline>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">Limited Seats. Maximum Impact.</h2>
            <p className="mt-3 text-navy/60">{w.label} pricing · {w.note}. {cur === "INR" ? "Indian delegates · 18% GST applies." : "International delegates · billed in USD, no GST."}</p>
          </div>
          <div data-testid="pricing-toggle" className="inline-flex rounded-full border border-border bg-white p-1">
            {[["INR", "₹ Indian"], ["USD", "$ International"]].map(([k, lbl]) => (
              <button key={k} data-testid={`pricing-cur-${k.toLowerCase()}`} onClick={() => setCur(k)}
                className={cn("rounded-full px-5 py-2 text-sm font-semibold transition-all", cur === k ? "bg-navy text-white" : "text-navy/60 hover:text-navy")}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-4">
          {tiers.map((t) => (
            <div key={t.key} data-testid="pricing-card"
              className={cn("relative flex flex-col rounded-3xl border-2 bg-white p-7 transition-all hover:-translate-y-1.5 hover:shadow-2xl",
                t.popular ? "border-lime bg-lime/5 shadow-xl lg:scale-[1.03]" : "border-border")}>
              {t.popular && <span data-testid="pricing-tier-popular" className="absolute -top-3 left-7 rounded-full bg-ethanolOrange px-3 py-1 text-xs font-bold uppercase tracking-wide text-navy">Most popular</span>}
              <p className="font-display text-lg font-semibold text-navy">{t.label}</p>
              <p className="mt-1 text-sm text-navy/55">{t.sub}</p>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-display text-4xl font-semibold tracking-tight text-emeraldDeep">{money(t.price, cur)}</span>
                {cur === "INR" && <span className="mb-1 text-xs text-navy/40">+ GST</span>}
              </div>
              <ul className="mt-5 flex-1 space-y-2.5">
                {t.includes.map((inc) => (
                  <li key={inc} className="flex items-start gap-2 text-sm text-navy/70">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald2" /> {inc}
                  </li>
                ))}
              </ul>
              <CTAButton to="/register" data-testid="pricing-checkout-btn" variant={t.popular ? "primary" : "emerald"} className="mt-6 w-full">Book Now</CTAButton>
            </div>
          ))}

          {/* agenda panel */}
          <div className="flex flex-col rounded-3xl border border-border bg-navy p-7 text-white lg:row-span-1">
            <p className="font-display text-lg font-semibold">Two Days. Transformative Conversations.</p>
            <div className="mt-4 space-y-4">
              {AGENDA_FLOW.map((d) => (
                <div key={d.day}>
                  <p className="text-xs font-bold uppercase tracking-wider text-lime">{d.day}</p>
                  <ul className="mt-1.5 space-y-1">
                    {d.items.map((i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300"><ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-lime" /> {i}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <ArrowLink to="/conference/agenda" light className="mt-5 text-sm">See the full agenda</ArrowLink>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <StatsBar />
      <PartnersMarquee />

      {/* WHO WILL YOU MEET */}
      <section data-testid="audience-section" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading center overline="Who will you meet?" title="A Power-Packed Gathering of India's Ethanol & Biofuel Ecosystem." />
          <Stagger className="mx-auto mt-12 grid max-w-5xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {AUDIENCE.map((a) => (
              <StaggerItem key={a.label}>
                <div data-testid="audience-card" className="group flex h-full flex-col items-center rounded-2xl border border-border bg-parchment p-6 text-center transition-all hover:-translate-y-1 hover:border-lime hover:shadow-lg">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald2/10 text-emeraldDeep transition-colors group-hover:bg-lime group-hover:text-navy"><a.icon className="h-6 w-6" /></span>
                  <p className="mt-4 font-display text-sm font-semibold leading-tight text-navy">{a.label}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* SPEAKERS + THEMES */}
      <section data-testid="speakers-section" className="relative overflow-hidden bg-navy grain py-20 md:py-28">
        <div className="dotgrid-dark pointer-events-none absolute inset-0 opacity-40" />
        <div className="pointer-events-none absolute -top-24 left-0 h-72 w-72 rounded-full bg-lime/10 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <Overline light className="mb-4">Hear from industry leaders</Overline>
              <h2 className="max-w-md font-display text-3xl font-semibold leading-[1.1] tracking-tight text-white sm:text-4xl">Industry Leaders &amp; Change Makers.</h2>
              <Stagger className="mt-9 flex flex-wrap items-center gap-5">
                {[0, 1, 2, 3].map((i) => (
                  <StaggerItem key={i}>
                    <div data-testid="speaker-card" className="flex flex-col items-center">
                      <div className="flex h-20 w-20 items-center justify-center rounded-full border border-dashed border-white/25 bg-white/[0.04] text-white/30"><Mic2 className="h-7 w-7" /></div>
                      <p className="mt-2 text-xs font-medium text-slate-400">To be announced</p>
                    </div>
                  </StaggerItem>
                ))}
                <div className="flex h-20 w-20 flex-col items-center justify-center rounded-full border border-lime/40 bg-lime/10 text-center">
                  <span className="font-display text-lg font-bold text-lime">30+</span>
                  <span className="text-[9px] uppercase tracking-wide text-slate-300">Speakers</span>
                </div>
              </Stagger>
              <p className="mt-7 max-w-md text-sm text-slate-400">The speaker line-up for the inaugural Bharat Ethanol Forum is being confirmed — announcements soon. Want to propose a speaker?</p>
              <ArrowLink to="/conference/speakers" light className="mt-4">View speakers &amp; propose</ArrowLink>
            </div>

            <div data-testid="themes-panel" className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 md:p-8">
              <p className="font-display text-xl font-semibold text-white">Key Discussion Themes</p>
              <ul className="mt-5 space-y-3">
                {THEMES.map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-lime/20 text-lime"><Check className="h-3 w-3" /></span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATOR (kept) */}
      <section id="calculator" className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Free interactive tool" title="See What Ethanol Blending Is Worth — In Crude, Rupees and CO₂." />
          <p className="mt-3 max-w-2xl text-base text-navy/60">Pick a blend level and a scale. The numbers update live — built on government-reported figures.</p>
          <div className="mt-8"><CalculatorTool /></div>
        </div>
      </section>

      {/* PRICING */}
      <Pricing />

      {/* EXPO BAND */}
      <section data-testid="expo-band" className="relative overflow-hidden bg-navy2 grain py-20 md:py-24">
        <img src="/assets/site/event-expo.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-center">
            <div>
              <Overline light className="mb-4">Concurrent expo</Overline>
              <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
                Ethanol &amp; Biofuels Trade &amp; Tech Expo <span className="text-lime">2026</span>
              </h2>
              <p className="mt-4 max-w-md text-slate-300">Showcase your solutions, technologies and innovations to India's most relevant audience. Showcase. Connect. Grow.</p>
              <CTAButton to="/exhibit" data-testid="expo-cta" variant="primary" className="mt-7">Book Your Booth <ArrowRight className="h-4 w-4" /></CTAButton>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { v: "50+", l: "Exhibiting Companies" },
                { v: "800+", l: "Industry Visitors" },
                { v: "2", l: "Full Days" },
                { v: "1:1", l: "B2B Matchmaking" },
              ].map((s) => (
                <div key={s.l} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                  <div className="font-display text-3xl font-semibold text-lime">{s.v}</div>
                  <p className="mt-1 text-sm text-slate-300">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROOF / WHY NOW */}
      <section data-testid="testimonials-section" className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading center overline="Why India, why now" title="The Industry Is Converging on Bharat 2026." />
          <Stagger className="mt-14 grid gap-6 lg:grid-cols-3">
            {PROOF.map((s) => (
              <StaggerItem key={s.stat}>
                <div data-testid="testimonial-card" className="flex h-full flex-col rounded-3xl border border-border bg-parchment p-8 shadow-sm">
                  <Quote className="h-7 w-7 text-emerald2/40" />
                  <p className="mt-4 font-display text-2xl font-semibold tracking-tight text-navy">{s.stat}</p>
                  <p className="mt-3 flex-1 text-navy/65">{s.line}</p>
                  <p className="mt-5 border-t border-border pt-4 text-xs font-medium text-navy/40">{s.src}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="mt-10 flex justify-center"><ArrowLink to="/insights/e20-to-e30-roadmap">Read the E20-to-E30 roadmap</ArrowLink></Reveal>
        </div>
      </section>

      {/* RESOURCES / LEAD MAGNETS */}
      <section className="border-y border-border bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Free downloads" title="Take the Forum's Intelligence With You." />
          <Stagger className="mx-auto mt-14 grid max-w-3xl gap-6 sm:grid-cols-2">
            {RESOURCES.map((r) => (
              <StaggerItem key={r.title}>
                <LeadDialog
                  formType={r.formType}
                  title={r.title}
                  description={r.desc}
                  ctaLabel={r.cta}
                  successMsg="Sent. Your download is starting now."
                  downloadUrl={r.doc ? DOCS[r.doc].url : undefined}
                  downloadName={r.doc ? DOCS[r.doc].name : undefined}
                  trigger={
                    <button data-testid={`resource-${r.formType}-${r.title.replace(/\W+/g, "-").toLowerCase()}`} className="group flex h-full w-full flex-col rounded-3xl border border-border bg-white p-7 text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
                      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald2/10 text-emeraldDeep transition-colors group-hover:bg-lime group-hover:text-navy"><r.icon className="h-6 w-6" /></span>
                      <h3 className="mt-5 font-display text-lg font-semibold text-navy">{r.title}</h3>
                      <p className="mt-2 flex-1 text-sm text-navy/60">{r.desc}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emeraldDeep transition-all group-hover:gap-3">{r.cta} <Download className="h-4 w-4" /></span>
                    </button>
                  }
                />
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* FINAL CTA BAND */}
      <section data-testid="final-cta-section" className="relative overflow-hidden bg-lime py-16 md:py-20">
        <div className="relative mx-auto flex max-w-7xl flex-col items-center justify-between gap-8 px-6 text-center md:flex-row md:px-12 md:text-left">
          <div>
            <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl">The Future of Fuel Is Being Built Today.</h2>
            <p className="mt-2 font-display text-xl font-semibold text-navy/70">Be in the founding room.</p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <CTAButton to="/register" data-testid="final-cta-btn" className="bg-navy px-8 py-4 text-white hover:bg-navy2 hover:-translate-y-0.5">Book Your Pass</CTAButton>
            <CTAButton to="/exhibit" variant="outline" className="border-navy px-8 py-4 text-navy hover:bg-navy/10">Exhibit / Sponsor</CTAButton>
          </div>
        </div>
      </section>

      {/* FAQ (AIO) */}
      <section className="border-t border-border bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <SectionHeading overline="Frequently asked" title="Everything You Need to Know." />
          <Accordion type="single" collapsible className="mt-10 rounded-2xl border border-border bg-white px-2">
            {HOME_FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-border" data-testid={`home-faq-${i}`}>
                <AccordionTrigger className="px-4 text-left font-display font-semibold text-navy hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="px-4 text-navy/70">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CtaStrip title="Put Your Name on India's Energy Transition." subtitle="Sponsorship and exhibitor slots for the first edition are limited. Secure your place early." />
    </>
  );
}
